import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createTextNode, $getSelection, $isRangeSelection} from 'lexical';
import React, {useCallback, useEffect, useState} from 'react';
import {Scrollbar} from '@priority-software/priority-style-react';

import DropDown, {DropDownItem} from '../../../ui/DropDown/DropDown';
import {createDataTestId} from '../../../../../../../../utils/dataTestId';

type Emoji = {
    emoji: string;
    description: string;
    category: string;
    aliases: Array<string>;
    tags: Array<string>;
    unicode_version: string;
    ios_version: string;
    skin_tones?: boolean;
};

type MappedEmojiData = {[key: string]: Array<Emoji>};

interface IEmojiPickerProps {
    portalSelector?: string;
    content?: string;
    disabled?: boolean;
}

export function EmojiPicker({portalSelector, content, disabled}: IEmojiPickerProps) {
    const [editor] = useLexicalComposerContext();
    const [emojiOptions, setEmojiOptions] = useState<MappedEmojiData>({});

    useEffect(() => {
        import('./emoji-list').then(emojis => {
            const data = emojis.default
                .map((item: Emoji) => ({...item, keywords: [...item.aliases, ...item.tags]}))
                .reduce((prev: MappedEmojiData, curr: Emoji) => {
                    if (prev[curr.category]) {
                        prev[curr.category].push(curr);
                    } else {
                        prev[curr.category] = [curr];
                    }
                    return prev;
                }, {});

            setEmojiOptions(data);
        });
    }, []);

    const onSelectOption = useCallback(
        (selectedOption: Emoji) => {
            editor?.update(() => {
                const selection = $getSelection();

                if (!$isRangeSelection(selection) || selectedOption == null) {
                    return;
                }

                selection.insertNodes([$createTextNode(selectedOption.emoji)]);
            });
        },
        [editor]
    );

    return (
        <DropDown
            {...createDataTestId(`toolbar-emoji-picker-dropdown`, false)}
            withPopover
            disabled={disabled}
            content={content}
            buttonLabel="🙂"
            buttonClassName="pui-emoji-picker"
            buttonAriaLabel="Emoji picker"
            portalSelector={portalSelector}
        >
            <div
                className="pui-emoji__menu"
                {...createDataTestId(`toolbar-emoji-picker-list-container`, false)}
            >
                <Scrollbar autoHeight autoHeightMax={300} autoHide={false}>
                    {Object.keys(emojiOptions).map(category => (
                        <div
                            className="pui-emoji__section"
                            key={category}
                            {...createDataTestId(`toolbar-emoji-picker-list-section`, false)}
                        >
                            <h6
                                {...createDataTestId(`toolbar-emoji-picker-list-title`, false)}
                                className="pui-emoji__heading"
                            >
                                {category}
                            </h6>

                            <div
                                className="pui-emoji__items-wrap"
                                {...createDataTestId(`toolbar-emoji-picker-list-box`, false)}
                            >
                                {emojiOptions[category].map((option: Emoji) => {
                                    return (
                                        <DropDownItem
                                            {...createDataTestId(
                                                `toolbar-emoji-picker-item-${option.description.replace(
                                                    ' ',
                                                    '-'
                                                )}`,
                                                false
                                            )}
                                            className="pui-emoji__item"
                                            onClick={() => onSelectOption(option)}
                                            key={option.description}
                                        >
                                            <span title={option.description}>{option.emoji}</span>
                                        </DropDownItem>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </Scrollbar>
            </div>
        </DropDown>
    );
}
